/**
 * Feed configuration for g1's live data path — resolves the set of upstream
 * Hydra-protocol feeds the tab should consume.
 *
 * Background: g1 historically read ALL sports from one firehose hydra over a
 * single SharedWorker. V3 ("satellite hydras") splits sports onto per-sport
 * edge engines (hydraedge), each serving the same consumer WS protocol on its
 * own URL. g1 must consume the firehose AND the edges simultaneously — the
 * firehose isn't going away. Rather than teach the SharedWorker to juggle N
 * upstream sockets (its connection loop is load-bearing and full of hard-won
 * stall/backpressure fixes), we run one SharedWorker instance PER feed (keyed
 * by `workerName`) and merge their frames into the single, source-agnostic
 * `marketStore`. See `useHydra` for the wiring.
 *
 * Ownership / cutover: each edge feed declares the sports it MAY own (its
 * static claim scope — `accepts` below). Live ownership is DYNAMIC, arbitrated
 * per frame in marketStore.feedShouldIngest: the edge is preferred whenever it
 * is healthy AND its live `activeSports` announce includes the sport; the
 * firehose is the warm fallback that resumes the sport the instant the edge
 * drops or stops serving it (the firehose keeps receiving every sport on its
 * broad subscribe, so failover needs no resubscribe). Exactly one feed ingests
 * a sport at any moment — both pipes serving it is redundancy, not flap — and
 * the edge's Feeds tuner drives cutover live: flip a sport primary on the edge
 * and g1 follows; block it (or lose the edge) and OG hydra takes it back.
 *
 * Config (env, build-time):
 *   VITE_HYDRA_URL          firehose consumer WS — REQUIRED, a core (vite.config.ts refuses an unset or OG value)
 *   VITE_HYDRA_EDGE_FEEDS   ';'-separated `[label@]url=sport,sport` entries,
 *                           e.g. `e1@wss://e1hydra.qeezi.com/ws=nfl` or
 *                           `e2@ws://localhost:8892/ws=nfl,mlb`. The optional
 *                           label is the engine's display identity (Live Feed
 *                           chips, health panels) — without it, edges render
 *                           by config POSITION as a slot name (`he-slot-1`,
 *                           `he-slot-2`, …), which says nothing about WHICH
 *                           engine the slot points at. The slot spelling is
 *                           deliberate: rule 9 reserves `he<n>`/`hc<n>` for
 *                           real instances, so a generated guess must not
 *                           borrow that syntax on a provenance surface.
 *                           Label your edges.
 *   VITE_HYDRA_CORE_FEEDS   same syntax, for hydracore consumer surfaces
 *                           (`c1@wss://core1.../ws=cricket,golf`). A core is a
 *                           full claimant in arbitration — same preferred-
 *                           owner / firehose-warm-fallback contract as an
 *                           edge. The boundary-rule-6 carve-out (core
 *                           shallow-tier sports are single-source, dark on
 *                           core outage) holds NATURALLY: OG's broad
 *                           subscribe carries no frames for sports it never
 *                           served, so fallback ingest finds nothing — and a
 *                           MIGRATED sport (one OG still broadcasts while its
 *                           core owns it) keeps OG as the warm net until OG
 *                           is finally retired. Unlabeled cores render
 *                           positionally as `hc-slot-1`, `hc-slot-2`, ….
 *   VITE_HYDRA_OP_AUTHORITY the PREFERRED source of the `oddspapi` channel
 *                           (the OP server migrated OG → hydracore,
 *                           2026-08-10/11). Three accepted forms:
 *                           - unset ⇒ the FIRST fleet core (VITE_HYDRA_CORE_FEEDS
 *                             order); no core ⇒ no OP lane. NEVER the OG
 *                             firehose — it carries no oddspapi lane
 *                             (Dan 2026-09-09), and naming it warns + falls
 *                             to the first core;
 *                           - a fleet feed ID (`firehose`, `core:0`,
 *                             `edge:1`) or that feed's exact consumer WS
 *                             URL — NOT a label (labels are unverified
 *                             operator claims; docs/hydra-ask-engine-
 *                             identity.md);
 *                           - a `[label@]ws(s)://` URL matching NO fleet
 *                             feed ⇒ a DEDICATED OP-only connection
 *                             (id `op:0`) is created for it — the hydracore
 *                             transport (`hc1@wss://hc1.../ws`). It speaks
 *                             only the oddspapi channel (no markets/
 *                             unabated/orderbooks) and claims no sports.
 *                           FAILOVER (Dan, 2026-08-11; OG removed from the
 *                           ladder 2026-09-09): the ladder is the preferred
 *                           core followed by the remaining fleet cores in
 *                           config order — every member holds the channel
 *                           subscribed (worker flags still latch at __init;
 *                           no runtime toggle THERE) and useHydra gates
 *                           ingestion to exactly ONE active source at any
 *                           instant, descending on trouble and climbing
 *                           back after the stability hold. With every core
 *                           down the lane holds its last committed values.
 *                           Each flip is a stager connection boundary
 *                           (generation namespace resets; committed state
 *                           survives; the re-requested snapshot rebases
 *                           epochs per contract rule 2e). OP authority is
 *                           ORTHOGONAL to sport claims: the active feed
 *                           ingests OP for ALL sports regardless of its
 *                           accepts() scope.
 *   VITE_HYDRA_CORE_TOKEN   plane principal bearer for CORE FLEET FEEDS —
 *                           appended as `?token=` to every
 *                           VITE_HYDRA_CORE_FEEDS URL (core consumer
 *                           surfaces are fail-closed, 401 without it).
 *                           Never applied to edges or the firehose; never
 *                           committed. When designating a core fleet feed
 *                           as the OP source, prefer its ID (`core:0`) over
 *                           URL matching — the URL carries the token.
 *   VITE_HYDRA_OP_TOKEN     plane principal bearer for the DEDICATED OP
 *                           connection (hydracore's consumer surface is
 *                           fail-closed, 401 without it). Browsers cannot
 *                           set WS headers, so it rides `?token=` on the
 *                           dedicated URL. Applied ONLY to the `op:0` feed —
 *                           never to fleet feeds. Env/config only, never
 *                           committed. NOTE a browser WS cannot READ the 401
 *                           status — a bad/absent token looks like a failed
 *                           handshake: the OP column chip goes red and OG
 *                           failover takes over; check the core's access log
 *                           to confirm 401 vs network.
 */

import { aliasSiblings, type Sport } from '@/views/ValueDashboard/types';
import { normalizeSport } from '@/stores/marketFrameNormalization';

export interface FeedConfig {
  /** Stable id used as the activeSports source key. */
  id: string;
  /** Consumer WS URL (browser-resolvable — the browser dials this, not the
   *  g1 container). */
  url: string;
  /** SharedWorker instance name — MUST be unique per feed, else two feeds
   *  share one worker (and one upstream URL). */
  workerName: string;
  /** 'core' = hydracore consumer surface. Identical claimant semantics to
   *  'edge' in arbitration/routing (marketStore treats every non-firehose
   *  feed as a claimant); the distinction is identity + display grouping. */
  role: 'firehose' | 'core' | 'edge';
  /** Display identity for feed-facing surfaces (Live Feed chips, health
   *  panels). Firehose is always 'OG'; cores/edges carry the env `label@`
   *  prefix when given, else undefined (consumers fall back to positional
   *  a positional SLOT name from the feed id — `he-slot-1`, `hc-slot-2` — which
   *  is deliberately not instance-identity syntax; see FeedTape.feedLabel). */
  label?: string;
  /** True when this feed should ingest a frame for `sport`. Core/edge feeds
   *  accept only the sports they own; the firehose accepts every sport NOT
   *  claimed by a core/edge (and frames with no/unknown sport — only the
   *  firehose is the catch-all). */
  accepts: (sport: Sport | null) => boolean;
  /** OP-CAPABLE (workspace:odds-papi-parallel-feed DNL-003, failover rev
   *  2026-08-11): this feed's worker subscribes the `oddspapi` channel and
   *  its client dispatches OP frames. True on every ladder member — the
   *  PREFERRED source and the other fleet cores; NEVER the OG firehose.
   *  Every non-capable feed's client DROPS OP frames with a counted
   *  diagnostic (belt and suspenders). The exactly-one-ingestion invariant
   *  moved UP a layer: useHydra gates dispatch to the single ACTIVE source
   *  (see `opPreferred`), so two feeds may subscribe but only one ever
   *  reaches the tab's OpSnapshotStager at any instant — the stager's
   *  epochs never interleave. */
  subscribeOpOdds: boolean;
  /** True on EXACTLY ONE feed — the env-designated preferred OP source
   *  (resolveOpAuthority; unset env ⇒ the first fleet core). useHydra ingests from it
   *  while its socket is up and fails over DOWN the ranked ladder (see
   *  `opRank`) when it drops. By construction: never two preferred. */
  opPreferred?: boolean;
  /** Position in the OP failover ladder — 0 is the preferred authority, then
   *  each remaining core in `VITE_HYDRA_CORE_FEEDS` order, with the firehose
   *  last (OG is the terminal standby by definition). Undefined on feeds that
   *  aren't OP-capable. The ladder is rooted at the designated authority: when
   *  that authority is the firehose there is nothing below it, so the ladder
   *  is the firehose alone — today's historical behavior, unchanged.
   *
   *  This is the DNS-resolver model Dan described (2026-08-14): hydracore
   *  instances are one uniform build differentiated by config, so the core
   *  list IS the preference order — hc1 first, hc2 second, fall down on
   *  trouble, climb back after a stability hold. A listed-but-not-yet-live
   *  core (hc2 on day one) costs nothing: promotion requires proof it is
   *  actually serving the channel, so it simply never wins the slot. */
  opRank?: number;
  /** Dedicated OP transport (the synthesized `op:0` feed): speaks ONLY the
   *  oddspapi channel — the worker/client skip markets + unabated
   *  subscribes, it claims no sports, and market-column UI ignores it. */
  opOnly?: boolean;
  /** DIRECTORY-DISCOVERED feeds only: widen this feed's claim scope from its
   *  own `activeSports` announce (G1-DIR-01).
   *
   *  A static feed's scope is an operator decision and stays fixed. A
   *  discovered edge's is not: the directory's `sports` list is ADVISORY and is
   *  frequently empty — an edge that has never reported to the publishing core
   *  is listed `live:false, sports:[]` precisely so it can be dialled before it
   *  announces. Snapshotting that empty list as the permanent scope made such
   *  an edge connect and then stay inert forever, because its later
   *  announcement was filtered out by the very scope the announcement should
   *  have set. Ownership is arbitrated from the engine's own announce — that is
   *  the authoritative signal, and this is how it reaches the claim set. */
  noteAnnouncedSports?: (sports: Sport[]) => void;
  /** Take on a directory-discovered edge's capability at this endpoint, when
   *  that edge moves onto a socket this feed already holds (G1-DIR-06). Opens
   *  the scope so later announces widen it too. Additive only — the operator's
   *  own claim is never narrowed. */
  absorbDirectoryCapability?: (sports: Iterable<Sport>) => void;
}

// ── Directory-discovered claims ─────────────────────────────────────────────
// Edges learned from a core's published directory are merged into the fleet
// AFTER resolveFeeds has run (the fetch is async; boot never waits on it —
// rule 6). Their sport claims therefore can't be baked into the firehose's
// static complement, so the firehose consults this live set instead. Without
// it a directory-discovered edge and the firehose would BOTH accept the same
// sport, which is double-ingest, not redundancy.
//
// Mutable by design: a directory refresh that newly reports an edge's sports
// activates the claim on the running fleet, because `accepts` closes over the
// set rather than a snapshot of it. resolveFeeds clears it — a fresh
// resolution starts from env, and the directory layer re-applies on top.
const _directoryClaimed = new Set<Sport>();

/** Register sports claimed by directory-discovered edges — from the directory
 *  payload at merge time, and from each discovered edge's own `activeSports`
 *  announce thereafter. Additive: the directory introduces engines, it never
 *  retracts a static claim. */
export function noteDirectoryClaims(sports: Iterable<Sport>): void {
  for (const s of sports) _directoryClaimed.add(s);
}

/** Sports currently claimed by directory-discovered edges. Read by
 *  `sportIsServable` so a dynamically discovered sport reaches navigation. */
export function directoryClaimedSports(): ReadonlySet<Sport> {
  return _directoryClaimed;
}

/**
 * A feed's claim scope: a fixed base plus an extensible set.
 *
 * Static feeds start CLOSED — their scope is operator config and an announce
 * cannot widen it. A directory-discovered feed starts OPEN, because the
 * directory's `sports` list is advisory and the engine's own announce is the
 * authority (G1-DIR-01).
 *
 * A static feed can be OPENED, once, by absorbing a directory-discovered edge
 * that moved onto its endpoint (G1-DIR-06). That is the case where two logical
 * capability contracts land on one physical socket: the operator says "from
 * this endpoint take MLB", the directory says "the edge here also serves NFL".
 * Collapsing them by keeping only the static half silently drops the
 * directory's capability — and for an edge-only sport, firehose fallback is not
 * an equivalent: OG may advertise the sport while carrying no game data for it.
 * So the capabilities UNION, which is what "additive in every direction" means
 * when the two claims are about the same socket. Widening only ever adds; the
 * operator's own claim can never be narrowed by a directory answer.
 */
export function makeClaimScope(base: ReadonlySet<Sport>, opts: { dynamic: boolean }) {
  const extra = new Set<Sport>();
  let dynamic = opts.dynamic;
  const widen = (sports: Iterable<Sport>) => {
    let grew = false;
    for (const sport of sports) {
      if (!base.has(sport) && !extra.has(sport)) { extra.add(sport); grew = true; }
    }
    // Registering the claim keeps the firehose off the sport (redundancy, not
    // double-ingest) and lets sportIsServable surface it in navigation.
    if (grew) noteDirectoryClaims(extra);
    return grew;
  };
  return {
    accepts: (sport: Sport | null) => sport != null && (base.has(sport) || extra.has(sport)),
    /** Take on a directory-discovered edge's capability at this endpoint. */
    absorbDirectoryCapability: (sports: Iterable<Sport>) => { widen(sports); dynamic = true; },
    /** Widen from this feed's own announce — only once the scope is open. */
    noteAnnouncedSports: (sports: Sport[]) => { if (dynamic) widen(sports); },
  };
}

function defaultHydraWsUrl(): string {
  // Consumer WS is on the API port (8891), not :443 (the public dashboard).
  // Local Vite is http://localhost, so plain ws to :8891 is allowed in dev.
  return import.meta.env.DEV ? 'ws://hydra.newbx.com:8891/ws' : 'wss://hydra.newbx.com/ws';
}

interface ParsedEdge {
  url: string;
  label?: string;
  ownsSports: Set<Sport>;
}

/** Parse `[label@]url=sport,sport;url2=sport` into edge descriptors. Malformed
 *  entries (no `=`, empty url/sports, no recognized sport) are skipped, not
 *  thrown — a typo in env shouldn't kill the whole live feed. The label is
 *  recognized only when what follows the `@` is a ws(s) URL, so a hypothetical
 *  userinfo `@` inside a URL can't be eaten as a label.
 *
 *  `allowSportless` (cores only) admits an entry that claims NO sports:
 *  `hc1@ws://host/ws=` or bare `hc1@ws://host/ws`. A hydracore instance can be
 *  a pure channel server — it serves `oddspapi` and claims no market sports —
 *  and before this it was unrepresentable: both forms were silently dropped,
 *  so the two-core failover config parsed to zero cores. Sportless is
 *  recognized ONLY from an empty sports part or a missing `=`, never from an
 *  unrecognized one: `ws://h/ws?token=abc` must not be mistaken for a
 *  sportless URL with a mangled tail, and a typo'd sport code must still be
 *  skipped loudly-in-effect rather than silently becoming a channel server. */
function parseEdgeFeeds(raw: string, opts: { allowSportless?: boolean } = {}): ParsedEdge[] {
  const out: ParsedEdge[] = [];
  for (const entry of raw.split(';').map((s) => s.trim()).filter(Boolean)) {
    // LAST '=' splits url from sports — the sports list never contains '=',
    // but a URL may (an inline `?token=` query). indexOf broke on those.
    const eq = entry.lastIndexOf('=');
    // No '=' at all: sportless when cores allow it, else malformed.
    if (eq < 0) {
      if (!opts.allowSportless) continue;
      const bare = parseLabeledWsUrl(entry);
      if (bare) out.push({ url: bare.url, label: bare.label, ownsSports: new Set<Sport>() });
      continue;
    }
    let url = entry.slice(0, eq).trim();
    let label: string | undefined;
    const at = url.indexOf('@');
    if (at > 0 && /^wss?:\/\//.test(url.slice(at + 1))) {
      label = url.slice(0, at).trim() || undefined;
      url = url.slice(at + 1).trim();
    }
    const sportsPart = entry.slice(eq + 1).trim();
    if (!url) continue;
    if (!sportsPart) {
      // Trailing '=' — an explicit "claims nothing" declaration.
      if (opts.allowSportless) out.push({ url, label, ownsSports: new Set<Sport>() });
      continue;
    }
    const ownsSports = new Set<Sport>();
    for (const code of sportsPart.split(',').map((s) => s.trim()).filter(Boolean)) {
      const s = normalizeSport(code);
      if (s) ownsSports.add(s);
    }
    if (ownsSports.size === 0) continue;
    out.push({ url, label, ownsSports });
  }
  return out;
}

/** Append a bearer as `?token=` when set (WS URLs can't carry headers from a
 *  browser). No-op on empty/undefined. */
function withToken(url: string, token: string | undefined): string {
  if (!token) return url;
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
}

/** Split an optional `label@` prefix off a ws(s) URL. Returns null when the
 *  value isn't a ws(s) URL (with or without prefix). */
function parseLabeledWsUrl(raw: string): { url: string; label?: string } | null {
  if (/^wss?:\/\//.test(raw)) return { url: raw };
  const at = raw.indexOf('@');
  if (at > 0 && /^wss?:\/\//.test(raw.slice(at + 1))) {
    return { url: raw.slice(at + 1).trim(), label: raw.slice(0, at).trim() || undefined };
  }
  return null;
}

/** Pick the PREFERRED OP source for a resolved fleet. Match is by feed ID or
 *  exact consumer WS URL — never by label (labels are unverified operator
 *  claims). A `[label@]ws(s)://` value matching NO fleet feed designates a
 *  DEDICATED OP-only connection (the hydracore transport): a synthesized
 *  `op:0` feed is APPENDED to `feeds` and returned — with the plane
 *  principal token (VITE_HYDRA_OP_TOKEN) riding `?token=` on its URL,
 *  because browsers cannot set WS headers. Fail-safe rules, warned so a
 *  miswired env is visible:
 *  - unset / unmatched non-URL value → the firehose (today's prod behavior);
 *  - a URL matching >1 fleet feeds → first in fleet order, deterministically.
 *  Exported for tests; callers get a member of `feeds`, never null. */
export function resolveOpAuthority(feeds: FeedConfig[]): FeedConfig | null {
  // OG carries NO oddspapi lane (Dan 2026-09-09: "I really don't think OG
  // has any OddsPapi lane at all. And if it does, you should turn that
  // off."). The firehose is never an OP source or standby; the default
  // authority is the first fleet core, and a fleet with no core has no OP
  // lane at all (every OP surface auto-hides — coverage semantics, rule 9).
  const firehose = feeds[0];
  const fallback = feeds.find((f) => f.role === 'core') ?? null;
  const raw = (import.meta.env.VITE_HYDRA_OP_AUTHORITY as string | undefined)?.trim();
  if (!raw) return fallback;
  const matches = feeds.filter((f) => f.id === raw || f.url === raw);
  if (matches.length > 0 && matches.every((f) => f === firehose)) {
    console.warn(
      `[feedConfig] VITE_HYDRA_OP_AUTHORITY="${raw}" names the OG firehose, which carries no oddspapi lane — ` +
      `OP authority falls to ${fallback ? fallback.id : 'NO feed (no fleet core configured)'}`,
    );
    return fallback;
  }
  if (matches.length > 1) {
    console.warn(
      `[feedConfig] VITE_HYDRA_OP_AUTHORITY="${raw}" matches ${matches.length} feeds ` +
      `(${matches.map((f) => f.id).join(', ')}) — first in fleet order (${matches[0].id}) wins`,
    );
  }
  if (matches.length > 0) return matches[0];

  const labeled = parseLabeledWsUrl(raw);
  if (labeled == null) {
    console.warn(
      `[feedConfig] VITE_HYDRA_OP_AUTHORITY="${raw}" matches no feed ` +
      `(ids: ${feeds.map((f) => f.id).join(', ')}) and is not a ws(s) URL — ` +
      `OP authority falls to ${fallback ? fallback.id : 'NO feed (no fleet core configured)'}`,
    );
    return fallback;
  }
  const token = (import.meta.env.VITE_HYDRA_OP_TOKEN as string | undefined)?.trim();
  const url = withToken(labeled.url, token);
  const dedicated: FeedConfig = {
    id: 'op:0',
    url,
    workerName: 'hydra-op-0',
    role: 'core', // it IS hydracore — chips render it in the core color
    label: labeled.label,
    opOnly: true,
    accepts: () => false, // claims no sports — OP transport only
    subscribeOpOdds: false, // stamped below like every feed
  };
  feeds.push(dedicated);
  return dedicated;
}

/** Resolve the live feeds from env. Always returns the firehose first, then
 *  cores, then edges — the fleet display order (OG | cores | edges), which is
 *  what the positional slot names are numbered from.
 *
 *  `readOnly: true` = a SIDE CONSUMER's lookup (the live game lane picking a
 *  URL): everything is computed fresh as usual, but the shared
 *  `_directoryClaimed` set is NOT cleared. Only the fleet's own (re)resolve
 *  in useHydra may clear it — a lane watchdog resolving every few seconds
 *  would otherwise silently widen the firehose's accepts() scope and
 *  double-ingest directory-claimed sports on the dashboard. */
export function resolveFeeds(opts?: { readOnly?: boolean }): FeedConfig[] {
  const firehoseUrl =
    (import.meta.env.VITE_HYDRA_URL as string | undefined)?.trim() || defaultHydraWsUrl();
  // Cores may claim no sports (pure channel servers — see parseEdgeFeeds);
  // edges must always claim at least one, so a typo'd edge stays skipped.
  const cores = parseEdgeFeeds(
    (import.meta.env.VITE_HYDRA_CORE_FEEDS as string | undefined) ?? '',
    { allowSportless: true },
  );
  const edges = parseEdgeFeeds((import.meta.env.VITE_HYDRA_EDGE_FEEDS as string | undefined) ?? '');

  // Union of core/edge-claimed sports — the firehose excludes these so a
  // claimed sport is served by exactly one feed.
  const claimed = new Set<Sport>();
  for (const e of [...cores, ...edges]) for (const s of e.ownsSports) claimed.add(s);
  // A fresh resolution starts from env; the directory layer re-applies its
  // discovered edges (and their claims) on top of the returned fleet.
  // Skipped for readOnly lookups — see the doc block above.
  if (!opts?.readOnly) _directoryClaimed.clear();

  const firehose: FeedConfig = {
    id: 'firehose',
    url: firehoseUrl,
    workerName: 'hydra-shared', // unchanged name → existing prod path untouched
    role: 'firehose',
    label: 'OG',
    accepts: (sport) => sport == null || (!claimed.has(sport) && !_directoryClaimed.has(sport)),
    subscribeOpOdds: false, // stamped below — exactly one authority
  };

  // Core consumer surfaces are uniformly bearer-gated (fail-closed 401) —
  // VITE_HYDRA_CORE_TOKEN rides ?token= on every core fleet feed's URL.
  // Edges/firehose never get it.
  const coreToken = (import.meta.env.VITE_HYDRA_CORE_TOKEN as string | undefined)?.trim();
  const coreFeeds: FeedConfig[] = cores.map((c, i) => ({
    id: `core:${i}`,
    url: withToken(c.url, coreToken),
    workerName: `hydra-core-${i}`,
    role: 'core' as const,
    label: c.label,
    ...makeClaimScope(c.ownsSports, { dynamic: false }),
    subscribeOpOdds: false, // stamped below — exactly one authority
  }));

  const edgeFeeds: FeedConfig[] = edges.map((e, i) => ({
    id: `edge:${i}`,
    url: e.url,
    workerName: `hydra-edge-${i}`,
    role: 'edge' as const,
    label: e.label,
    ...makeClaimScope(e.ownsSports, { dynamic: false }),
    subscribeOpOdds: false, // stamped below — exactly one authority
  }));

  // OP source designation (DNL-003, failover rev): at most one PREFERRED
  // feed by construction — a fleet core (or the dedicated op:0 transport).
  // The OG firehose is NEVER OP-capable (Dan 2026-09-09): it carries no
  // oddspapi lane, so it is neither an authority nor a standby. useHydra
  // gates ingestion to one active source at any instant (env doc block).
  const feeds = [firehose, ...coreFeeds, ...edgeFeeds];
  const preferred = resolveOpAuthority(feeds); // may append a dedicated op:0 feed
  if (preferred == null) return feeds; // no core ⇒ no OP lane at all
  preferred.opPreferred = true;

  // The OP failover LADDER, rooted at the designated authority:
  //   [preferred, ...remaining fleet cores in rank order]
  // Rank = position in VITE_HYDRA_CORE_FEEDS (Dan's resolver model — hc1
  // first, hc2 second). The chain extends through the other cores only when
  // the authority is itself a fleet core; the synthesized op:0 transport is
  // a standalone lane whose peers are the sports-claiming cores, not
  // alternates for it. There is no terminal standby: when every core is
  // down the lane keeps its last committed values and the GameDetail badge
  // says "no source" — never a moneyline-only echo from OG.
  const preferredIsFleetCore = preferred.role === 'core' && preferred.opOnly !== true;
  const ladder: FeedConfig[] = [preferred];
  if (preferredIsFleetCore) {
    for (const c of coreFeeds) if (c !== preferred) ladder.push(c);
  }
  ladder.forEach((feed, rank) => {
    feed.opRank = rank;
    // Capability is constructor-fixed on HydraClient and the SharedWorker's
    // `oddspapi` subscribe latches irrevocably per worker name, so every
    // ladder member subscribes for the session. Only the ACTIVE one is ever
    // dispatched into the stager (useHydra's active-source gate).
    feed.subscribeOpOdds = true;
  });
  return feeds;
}

// Sports whose live game feed comes ONLY from a hydraedge edge in the V3 split
// (Phase 1 = NFL). Prod hydra's `activeSports` meta frame still advertises them
// — /api/v1/active-sports lists "NFL" — but a firehose-only deploy never
// receives their game data, so the dashboard would render a dead-end chip. The
// toolbar surfaces these only when an edge feed actually owns the sport in THIS
// build (see `sportIsServable`). This is a build-time config gate, NOT a
// per-connect market-discovery scan, so it doesn't reintroduce the snapshot cost
// the meta-frame auto-hide was built to avoid.
export const EDGE_ONLY_SPORTS: ReadonlySet<Sport> = new Set<Sport>(['nfl']);

/** Edge-owned sports for this build, parsed from env. Empty on a firehose-only
 *  deploy. Env is build-time static in prod; reading at call time (rather than
 *  caching at module load) keeps it consistent with `resolveFeeds` and unit
 *  testable via `vi.stubEnv`. */
function edgeServedSports(): Set<Sport> {
  const owned = new Set<Sport>();
  for (const e of parseEdgeFeeds((import.meta.env.VITE_HYDRA_EDGE_FEEDS as string | undefined) ?? '')) {
    for (const s of e.ownsSports) owned.add(s);
  }
  return owned;
}

/** Whether this deploy can actually surface `sport` as a usable chip. Edge-only
 *  sports (NFL) are hidden unless an edge feed serves them here; every other
 *  sport is always servable (the firehose carries it). The cheap
 *  `EDGE_ONLY_SPORTS` short-circuit means the env parse only runs for the
 *  edge-only sports, so this is fine to call per chip render. */
export function sportIsServable(sport: Sport): boolean {
  if (!EDGE_ONLY_SPORTS.has(sport)) return true;
  // Static env config OR a live directory claim (G1-DIR-02). Without the
  // second, an edge-only sport served ONLY by a discovered edge is stripped
  // from navigation — the chip never appears, so the sport is unreachable even
  // though the fleet is connected to an engine serving it. That defeats
  // "adding an edge touches nothing in g1": it would still need an env edit.
  // A flow label (NFLPROPS) served by any edge makes its parent tab reachable
  // too — the tab renders every alias sibling.
  const served = edgeServedSports();
  const claimed = directoryClaimedSports();
  return aliasSiblings(sport).some((s) => served.has(s) || claimed.has(s));
}
